import { Chain, Status, StatusCode } from "database";
import { FC, useMemo, useCallback } from "react";
import { getEnvironment } from "utils/env";
import { Beta_Chains } from "utils/betaChains";
import { useAuth } from "@hooks";
import { trpc } from "@utils";
import UpdateWalletAction from "@components/wallet/action-buttons/UpdateWalletAction";
import ActionsWrapper from "@components/wallet/action-buttons/ActionsWrapper";
import { useNotification } from "hooks/useNotification";
import useContact from "hooks/useContact";
import DeleteAction from "@components/wallet/action-buttons/DeleteAction";

interface Props {
    status: Status;
    id: string;
    chain: Chain;
    address: string;
}

const TrackedWalletTableActions: FC<Props> = ({
    status,
    id,
    chain,
    address,
}) => {
    const { session } = useAuth();
    const notify = useNotification();
    const utils = trpc.useContext();

    const { contactNameOrAddress } = useContact({
        walletAddress: address,
        chain,
    });

    const actionDisabled = useMemo(
        () =>
            status.code === StatusCode.RUNNING ||
            status.code === StatusCode.IDLE ||
            status.code === StatusCode.INDEXING,
        [status.code],
    );

    const isBetaChainWallet =
        getEnvironment() === "production" &&
        !session?.isAdmin &&
        Beta_Chains.includes(chain);

    const { mutateAsync: deleteWalletCallback } =
        trpc.trackedWallet.delete.useMutation({
            onMutate: () => {
                notify.loading("Deleting wallet");
            },
            onSuccess: () => {
                notify.success("Deleted tracked wallet");
                utils.trackedWallet.list.invalidate();
                utils.transaction.invalidate();
            },
            onError: (error) => {
                console.error(error);
                notify.error(
                    `Failed to delete tracked wallet: ${error.message}`,
                );
                utils.trackedWallet.list.invalidate();
            },
        });

    const handleDelete = useCallback(async () => {
        await deleteWalletCallback({ id });
    }, [deleteWalletCallback, id]);

    const {
        mutateAsync: invokeProcessingCallback,
        isLoading: isInvokeLoading,
    } = trpc.trackedWallet.invokeProcessing.useMutation({
        onSuccess: () => {
            utils.trackedWallet.list.invalidate();
            notify.success("Started processing transactions");
        },
        onError: (err) => {
            notify.error(
                err.data?.code === "FORBIDDEN"
                    ? err.message
                    : "Cannot process transactions at the moment, please try again later",
            );
        },
    });

    const handleUpdate = useCallback(async () => {
        invokeProcessingCallback({
            isHardReset: false,
            trackedWalletId: id,
        });
    }, [invokeProcessingCallback, id]);

    return (
        <ActionsWrapper>
            <UpdateWalletAction
                disabledText={
                    isBetaChainWallet
                        ? "Beta chain wallets cannot be self-updated. Please reach out to our team if you wish to update your wallet."
                        : "Wait for the current processing to finish before re-processing the wallet"
                }
                disabled={isBetaChainWallet || actionDisabled}
                isLoading={isInvokeLoading}
                handleUpdate={handleUpdate}
            />

            <DeleteAction
                type="wallet"
                disabled={actionDisabled}
                handleDelete={handleDelete}
                name={contactNameOrAddress}
                description="Deleting this wallet will remove its data from future cost
                    basis calculations and reports. Historical reports will
                    remain unchanged. Do you wish to proceed?"
            />
        </ActionsWrapper>
    );
};

export default TrackedWalletTableActions;
